import React, { useEffect, useState, useCallback } from "react";
import { Plus, Eye, EyeOff, Copy, Trash2, Pencil, Ban } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { formatKobo } from "../../lib/format";
import { useSettings } from "../../context/SettingsContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import CateringManager from "./CateringManager";

const ALLERGENS = ["Contains Egg", "Contains Dairy", "Contains Gluten", "Contains Pork", "Contains Nuts"];
const BADGES = ["New", "Spicy", "Best Seller"];

export default function MenuManager() {
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [catModal, setCatModal] = useState(false);
  const [newCat, setNewCat] = useState({ name: "", display_name: "" });
  const { refresh } = useSettings();
  const [tab, setTab] = useState("breakfast");

  const load = useCallback(() => {
    api.get("/admin/categories").then(({ data }) => setCategories(data));
    api.get("/admin/items").then(({ data }) => setItems(data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const itemsByCat = (cid) => items.filter((i) => i.category_id === cid).sort((a, b) => a.order - b.order);

  const patchItem = async (item, changes) => {
    try {
      const { data } = await api.put(`/admin/items/${item.id}`, changes);
      setItems((prev) => prev.map((i) => (i.id === item.id ? data : i)));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeItem = async (item) => {
    if (!window.confirm(`Delete ${item.name}?`)) return;
    await api.delete(`/admin/items/${item.id}`);
    setItems((prev) => prev.filter((i) => i.id !== item.id));
    toast.success("Item deleted");
  };

  const duplicate = async (item) => {
    const { data } = await api.post(`/admin/items/${item.id}/duplicate`);
    setItems((prev) => [...prev, data]);
    toast.success("Item duplicated");
  };

  const saveItem = async (form) => {
    try {
      if (form.id) {
        const { data } = await api.put(`/admin/items/${form.id}`, form);
        setItems((prev) => prev.map((i) => (i.id === form.id ? data : i)));
      } else {
        const { data } = await api.post("/admin/items", form);
        setItems((prev) => [...prev, data]);
      }
      setEditing(null);
      toast.success("Saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addCategory = async () => {
    if (!newCat.name) return toast.error("Enter a name");
    const { data } = await api.post("/admin/categories", { name: newCat.name, display_name: newCat.display_name || newCat.name });
    setCategories((prev) => [...prev, data]);
    setCatModal(false);
    setNewCat({ name: "", display_name: "" });
    refresh();
  };

  const toggleCatHidden = async (c) => {
    const { data } = await api.put(`/admin/categories/${c.id}`, { hidden: !c.hidden });
    setCategories((prev) => prev.map((x) => (x.id === c.id ? data : x)));
    refresh();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <button data-testid="menu-tab-breakfast" onClick={() => setTab("breakfast")} className={`px-4 py-2 rounded-full text-sm font-semibold ${tab === "breakfast" ? "bg-sapore-purple text-sapore-cream" : "border border-sapore-border text-sapore-inkSoft"}`}>Breakfast Menu</button>
          <button data-testid="menu-tab-catering" onClick={() => setTab("catering")} className={`px-4 py-2 rounded-full text-sm font-semibold ${tab === "catering" ? "bg-sapore-purple text-sapore-cream" : "border border-sapore-border text-sapore-inkSoft"}`}>Catering Menu</button>
        </div>
        {tab === "breakfast" && <button data-testid="add-category-button" onClick={() => setCatModal(true)} className="text-sm px-4 py-2 rounded-full border border-sapore-purple text-sapore-purple font-medium hover:bg-sapore-goldSubtle">+ Category</button>}
      </div>

      {tab === "catering" && <CateringManager />}

      {tab === "breakfast" && <div className="space-y-8">
        {categories.map((c) => (
          <section key={c.id} data-testid={`admin-category-${c.slug}`}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-serif text-xl text-sapore-purple flex items-center gap-2">
                {c.display_name} {c.hidden && <span className="text-xs bg-sapore-muted/20 text-sapore-muted px-2 py-0.5 rounded-full">Hidden</span>}
              </h2>
              <div className="flex gap-2">
                <button onClick={() => toggleCatHidden(c)} className="text-xs text-sapore-inkSoft hover:text-sapore-purple flex items-center gap-1">{c.hidden ? <Eye size={14} /> : <EyeOff size={14} />}{c.hidden ? "Show" : "Hide"}</button>
                <button data-testid={`add-item-${c.slug}`} onClick={() => setEditing({ category_id: c.id, name: "", description: "", price: 0, variants: [], allergens: [], badges: [], image_url: "", prep_note: "", visible: true, sold_out: false })} className="text-xs px-3 py-1 rounded-full bg-sapore-purple text-sapore-cream flex items-center gap-1"><Plus size={13} /> Item</button>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {itemsByCat(c.id).map((item) => (
                <div key={item.id} data-testid={`admin-item-${item.id}`} className="bg-white rounded-xl border border-sapore-border p-3 flex gap-3">
                  <div className="w-14 h-14 rounded-lg overflow-hidden bg-sapore-goldSubtle flex-shrink-0 flex items-center justify-center">
                    {item.image_url ? <img src={item.image_url} alt="" className="w-full h-full object-cover" /> : <span className="text-[9px] text-sapore-gold font-bold">SAPORE</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-sapore-ink truncate">{item.name}</p>
                    <p className="text-xs text-sapore-gold font-semibold">{item.variants?.length ? `${formatKobo(item.variants[0].price)}+` : formatKobo(item.price)}</p>
                    <div className="flex gap-1 mt-1.5 flex-wrap">
                      <button data-testid={`toggle-visible-${item.id}`} onClick={() => patchItem(item, { visible: !item.visible })} title="Visible" className={`p-1 rounded ${item.visible ? "text-sapore-purple" : "text-sapore-muted"}`}>{item.visible ? <Eye size={14} /> : <EyeOff size={14} />}</button>
                      <button data-testid={`toggle-soldout-${item.id}`} onClick={() => patchItem(item, { sold_out: !item.sold_out })} title="Sold out" className={`p-1 rounded ${item.sold_out ? "text-red-600" : "text-sapore-muted"}`}><Ban size={14} /></button>
                      <button data-testid={`edit-item-${item.id}`} onClick={() => setEditing(item)} className="p-1 text-sapore-inkSoft hover:text-sapore-purple"><Pencil size={14} /></button>
                      <button onClick={() => duplicate(item)} className="p-1 text-sapore-inkSoft hover:text-sapore-purple"><Copy size={14} /></button>
                      <button data-testid={`delete-item-${item.id}`} onClick={() => removeItem(item)} className="p-1 text-sapore-inkSoft hover:text-red-600"><Trash2 size={14} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>}

      {editing && <ItemEditor item={editing} onClose={() => setEditing(null)} onSave={saveItem} />}

      <Dialog open={catModal} onOpenChange={setCatModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-serif text-xl text-sapore-purple">New category</DialogTitle></DialogHeader>
          <input data-testid="new-cat-name" placeholder="Name (e.g. Lunch)" value={newCat.name} onChange={(e) => setNewCat({ ...newCat, name: e.target.value })} className="w-full rounded-xl border border-sapore-border p-3 text-sm mb-3" />
          <input data-testid="new-cat-display" placeholder="Display name" value={newCat.display_name} onChange={(e) => setNewCat({ ...newCat, display_name: e.target.value })} className="w-full rounded-xl border border-sapore-border p-3 text-sm mb-4" />
          <button data-testid="save-cat" onClick={addCategory} className="w-full py-3 rounded-full bg-sapore-gold text-sapore-ink font-semibold">Create</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ItemEditor({ item, onClose, onSave }) {
  const [form, setForm] = useState({ ...item, price: item.price ? item.price / 100 : "", variants: item.variants || [], allergens: item.allergens || [], badges: item.badges || [] });
  const up = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1500000) return toast.error("Image too large (max 1.5MB)");
    const reader = new FileReader();
    reader.onload = () => up("image_url", reader.result);
    reader.readAsDataURL(file);
  };

  const toggleArr = (key, val) => {
    setForm((f) => ({ ...f, [key]: f[key].includes(val) ? f[key].filter((x) => x !== val) : [...f[key], val] }));
  };

  const save = () => {
    const payload = {
      ...form,
      price: form.variants.length ? null : Math.round((parseFloat(form.price) || 0) * 100),
      variants: form.variants.map((v) => ({ name: v.name, price: typeof v.price === "string" ? Math.round(parseFloat(v.price) * 100) : v.price })),
    };
    onSave(payload);
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto no-scrollbar" data-testid="item-editor">
        <DialogHeader><DialogTitle className="font-serif text-xl text-sapore-purple">{item.id ? "Edit item" : "New item"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <input data-testid="item-name-input" placeholder="Name" value={form.name} onChange={(e) => up("name", e.target.value)} className="w-full rounded-xl border border-sapore-border p-3 text-sm" />
          <textarea data-testid="item-desc-input" placeholder="Description" value={form.description} onChange={(e) => up("description", e.target.value)} rows={2} className="w-full rounded-xl border border-sapore-border p-3 text-sm" />
          {form.variants.length === 0 && (
            <div>
              <label className="text-sm font-medium text-sapore-ink">Price (₦)</label>
              <input data-testid="item-price-input" type="number" value={form.price} onChange={(e) => up("price", e.target.value)} className="w-full rounded-xl border border-sapore-border p-3 text-sm mt-1" />
            </div>
          )}

          <div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-sapore-ink">Size variants</label>
              <button onClick={() => up("variants", [...form.variants, { name: "", price: "" }])} className="text-xs text-sapore-purple">+ Add variant</button>
            </div>
            {form.variants.map((v, i) => (
              <div key={i} className="flex gap-2 mt-2">
                <input placeholder="Size (e.g. 350ml)" value={v.name} onChange={(e) => { const nv = [...form.variants]; nv[i] = { ...nv[i], name: e.target.value }; up("variants", nv); }} className="flex-1 rounded-lg border border-sapore-border p-2 text-sm" />
                <input placeholder="₦ price" type="number" value={typeof v.price === "number" ? v.price / 100 : v.price} onChange={(e) => { const nv = [...form.variants]; nv[i] = { ...nv[i], price: e.target.value }; up("variants", nv); }} className="w-28 rounded-lg border border-sapore-border p-2 text-sm" />
                <button onClick={() => up("variants", form.variants.filter((_, x) => x !== i))} className="text-red-500"><Trash2 size={16} /></button>
              </div>
            ))}
          </div>

          <div>
            <label className="text-sm font-medium text-sapore-ink block mb-1.5">Image</label>
            <input data-testid="item-image-input" type="file" accept="image/*" onChange={handleImage} className="text-sm" />
            {form.image_url && <img src={form.image_url} alt="" className="mt-2 w-24 h-18 rounded-lg object-cover" />}
          </div>

          <div>
            <label className="text-sm font-medium text-sapore-ink block mb-1.5">Allergens</label>
            <div className="flex flex-wrap gap-1.5">
              {ALLERGENS.map((a) => (
                <button key={a} onClick={() => toggleArr("allergens", a)} className={`text-xs px-2.5 py-1 rounded-full border ${form.allergens.includes(a) ? "bg-sapore-purple text-sapore-cream border-sapore-purple" : "border-sapore-border text-sapore-ink"}`}>{a}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-sapore-ink block mb-1.5">Badges</label>
            <div className="flex flex-wrap gap-1.5">
              {BADGES.map((b) => (
                <button key={b} onClick={() => toggleArr("badges", b)} className={`text-xs px-2.5 py-1 rounded-full border ${form.badges.includes(b) ? "bg-sapore-gold text-sapore-ink border-sapore-gold" : "border-sapore-border text-sapore-ink"}`}>{b}</button>
              ))}
            </div>
          </div>
          <input placeholder="Prep-time note (optional)" value={form.prep_note} onChange={(e) => up("prep_note", e.target.value)} className="w-full rounded-xl border border-sapore-border p-3 text-sm" />
          <button data-testid="save-item-button" onClick={save} className="w-full py-3 rounded-full bg-sapore-gold text-sapore-ink font-semibold hover:bg-sapore-goldHover">Save item</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
